import { useMemo, useState, type ChangeEvent } from "react";
import { Sidebar } from "primereact/sidebar";
import { TabView, TabPanel } from "primereact/tabview";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import "./style.scss";
import CustomeButton from "../Button";
import SearchBar from "~/components/searchBar";
import useDocumentDescription from "./useDocumentDescription";
import { Tree } from "primereact/tree";
import { Badge } from "primereact/badge";
import { useNavigate } from "react-router-dom";
import DocumentHistory from "./DocumentHistory";

const NonEditableField = ({
  label,
  value,
  icon,
  onclickHandler,
}: {
  label: string;
  value: string | null;
  icon?: string;
  onclickHandler?: () => void;
}) => (
  <div className="file-detail-row">
    <div className="file-detail-label">
      <span className="label">{label}</span>
    </div>
    <div
      className={`file-detail-value file-detail-value${icon ? "--enabled" : "--locked"}`}
    >
      <span>{value || "-"}</span>
      {icon && (
        <i
          className={icon}
          style={{ marginLeft: "0.5rem" }}
          onClick={onclickHandler}
        />
      )}
    </div>
  </div>
);

const DocumentDescription = ({
  data,
  id,
  type,
  fileName = "",
  visible,
  onVisibleChange,
  hideTrigger = false,
  onDescriptionUpdated,
  onDataUpdate,
}: {
  data: any;
  id: string;
  type: string;
  fileName?: string;
  visible?: boolean;
  onVisibleChange?: (v: boolean) => void;
  hideTrigger?: boolean;
  onDescriptionUpdated?: (row: any, newDesc?: string) => void;
  onRefetch?: () => void;
  onDataUpdate?: (updatedRow: any) => void;
}) => {
  const {
    description,
    setDescription,
    descriptionSuccessMessage,
    safeDecode,
    formatDate,
    canEdit,
    isGxmAdmin,
    isEditMode,
    activeTab,
    setActiveTab,
    fileSubCategory,
    fileYear,
    fileMonth,
    fileMonthYear,
    lastModified,
    saving,
    showCategories,
    setShowCategories,
    filteredCategories,
    categoryAccordionRef,
    categoryName,
    subCategoryName,
    hasDescriptionChange,
    sidebarVisible,
    setSidebarVisible,
    isControlled,
    searchCategory,
    handleCategorySelect,
    handleSubCategorySelect,
    handleDescriptionSave,
    handleDescriptionDiscard,
    handleMonthYearChange,
    handleViewInBrowser,
    handleDownloadFile,
    openEditMode,
    setOpen,
    months,
    metadataErrorMessage,
    categorySaving,
    dateSaving,
  } = useDocumentDescription({
    data,
    id,
    type,
    visible,
    onVisibleChange,
    onDescriptionUpdated,
    onDataUpdate,
  });
  const navigate = useNavigate();

  const [expandedCategoryCode, setExpandedCategoryCode] = useState<
    string | null
  >(null);

  const handleCategoryToggle = (cat: { code: string }) => {
    setExpandedCategoryCode((prev) =>
      prev === cat?.code ? null : (cat?.code ?? null),
    );
  };

  const handleSubCategorySelectAndClose = (
    parentCat: { code: string; name: string },
    sub: { code: string; name: string },
  ) => {
    handleCategorySelect(parentCat);
    handleSubCategorySelect(sub);
  };

  // Compute a dynamic display name from the current category/date state
  // so the sidebar header and "Renamed to" update reactively when the user changes the category
  const dynamicDisplayName = useMemo(() => {
    if (!subCategoryName) return null;

    // Get the extension from the original file name
    const originalName = data?.original_file_name || data?.name || "";
    const decoded = safeDecode(originalName);
    const lastDot = decoded.lastIndexOf(".");
    const extension =
      lastDot > 0 ? decoded.slice(lastDot + 1).toLowerCase() : "";

    let baseName = subCategoryName;

    if (fileYear && fileMonth) {
      const date = new Date(Number(fileYear), Number(fileMonth) - 1);
      const monthName = date.toLocaleString("default", { month: "long" });
      baseName = `${subCategoryName} - ${monthName} ${fileYear}`;
    }

    return extension ? `${baseName}.${extension}` : baseName;
  }, [
    subCategoryName,
    fileYear,
    fileMonth,
    data?.original_file_name,
    data?.name,
  ]);

  const renamedFileName =
    dynamicDisplayName ||
    data?.displayName ||
    data?.metadata?.display_name ||
    fileName ||
    data?.fileName ||
    data?.name ||
    null;

  const customHeader = (
    <div className="flex flex-column sidebar-header flex align-items-center gap-1">
      <h3 className="m-0 sidebar-title">
        {safeDecode(
          renamedFileName || data?.original_file_name || data?.name || "",
        )}
      </h3>
      {data.is_published && (
        <Badge
          className="publish-badge"
          value={
            type === "PROCESS"
              ? "Publish to Entity Files"
              : "Published via Process"
          }
        />
      )}
    </div>
  );

  const getHeaderName = () => {
    return `${data?.processDetails?.name} ${getDeadlineDate(data?.processDetails?.start_date)}${getSequenceNumber(data?.processDetails?.sequence)}`;
  };
  const getDeadlineDate = (start_date: string) => {
    if (start_date) {
      const deadline =
        new Date(start_date).getFullYear() +
        new Date(start_date).toLocaleDateString("default", {
          month: "2-digit",
        });
      return deadline;
    }
  };

  const navigateToProcess = () => {
    navigate(`/process/${data?.processDetails?.id}/milestones`);
  };
  const getSequenceNumber = (sequence: string) => {
    if (sequence) {
      return `_${sequence}`;
    }
    return "";
  };

  if (isControlled && !data) return null;

  const detailsContent = (
    <div className="details-content">
      <div className="description-section">
        <div className="description-section__header">
          <span className="section-label">Description</span>
          {canEdit && !isEditMode && (
            <CustomeButton
              label="Edit"
              variant="outlined"
              icon="pi pi-pencil"
              iconPos="right"
              className="edit-btn"
              onClick={openEditMode}
            />
          )}
        </div>
        <div className="description-section__content">
          {isEditMode && canEdit ? (
            <>
              <textarea
                className="description-input"
                value={safeDecode(description || "")}
                onChange={(e) =>
                  setDescription(e.target.value.substring(0, 200))
                }
                placeholder="Add description to file"
                maxLength={200}
              />
              <p className="text__limit">
                {safeDecode(description || "").length || 0}/200
              </p>
            </>
          ) : (
            <p className="description-readonly">
              {safeDecode(
                description || data?.description || "Add description to file",
              )}
            </p>
          )}
          {isEditMode && canEdit && (
            <div className="description-actions">
              <CustomeButton
                label="Discard"
                variant="outlined"
                onClick={handleDescriptionDiscard}
                disabled={saving}
              />
              <CustomeButton
                label="Save Description"
                variant="contained"
                icon="pi pi-check"
                iconPos="right"
                onClick={handleDescriptionSave}
                loading={saving}
                disabled={!hasDescriptionChange}
              />
            </div>
          )}
          {descriptionSuccessMessage && (
            <div className="description-success-banner">
              <i className="pi pi-check-circle success-icon" />
              <span>{descriptionSuccessMessage}</span>
            </div>
          )}
        </div>
      </div>
      {metadataErrorMessage && (
        <div className="metadata-error-banner">
          <i className="pi pi-times-circle error-icon" />
          <span>{metadataErrorMessage}</span>
        </div>
      )}
      <div className="file-details-section">
        <div className="section-header">
          <span className="section-label">File Details</span>
        </div>
        <div className="file-details-content">
          {type === "ENTITIES_PLUS" && data?.is_published && (
            <NonEditableField
              label="File Source"
              value={getHeaderName()}
              icon={"pi pi-external-link"}
              onclickHandler={navigateToProcess}
            />
          )}
          <NonEditableField
            label="File Original Name"
            value={
              data?.original_file_name
                ? safeDecode(data.original_file_name)
                : null
            }
          />
          <NonEditableField
            label="Renamed to"
            value={renamedFileName ? safeDecode(renamedFileName) : null}
          />
          <div className="file-detail-row file-detail-row--category">
            <div className="file-detail-label">
              <span className="label">Category*</span>
            </div>
            {canEdit ? (
              <div
                className="details-field-container category-tree-wrapper"
                ref={categoryAccordionRef}
              >
                <Button
                  outlined
                  disabled={saving || categorySaving}
                  icon={
                    categorySaving
                      ? "pi pi-spin pi-spinner"
                      : "pi pi-angle-down"
                  }
                  iconPos="right"
                  className="category-dropdown-btn"
                  onClick={() => {
                    setShowCategories((prev) => !prev);
                  }}
                >
                  <span className="category-selected">
                    {subCategoryName || categoryName || "Select Category"}
                  </span>
                </Button>
                {showCategories && (
                  <div className="category-container category-tree-container">
                    <div className="search-container">
                      <SearchBar
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          searchCategory(e.target.value)
                        }
                        placeholder="Search categories..."
                      />
                    </div>
                    <ul className="category-tree">
                      {filteredCategories.length > 0 ? (
                        filteredCategories.map((cat, index) => (
                          <li
                            key={cat?.code ?? index}
                            className="category-tree-node"
                          >
                            <div
                              className={`category-tree-parent ${expandedCategoryCode === cat?.code ? "expanded" : ""}`}
                              onClick={() => handleCategoryToggle(cat)}
                            >
                              <i
                                className={`pi ${expandedCategoryCode === cat?.code ? "pi-chevron-down" : "pi-chevron-right"}`}
                              />
                              <i
                                className={`pi ${expandedCategoryCode === cat?.code ? "pi-folder-open" : "pi-folder"}`}
                              />
                              <span>{cat?.name}</span>
                            </div>
                            {expandedCategoryCode === cat?.code && (
                              <ul className="category-tree-children">
                                {cat?.subCategory?.map(
                                  (
                                    sub: { code: string; name: string },
                                    subIndex: number,
                                  ) => (
                                    <li
                                      key={sub?.code ?? subIndex}
                                      className={`category-tree-child ${fileSubCategory === sub?.code ? "selected" : ""}`}
                                      onClick={() =>
                                        handleSubCategorySelectAndClose(
                                          cat,
                                          sub,
                                        )
                                      }
                                    >
                                      <i className="pi pi-folder" />
                                      <span>{sub?.name}</span>
                                    </li>
                                  ),
                                )}
                              </ul>
                            )}
                          </li>
                        ))
                      ) : (
                        <p className="empty-search">No results found</p>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="file-detail-value">
                {subCategoryName || categoryName || "-"}
              </div>
            )}
          </div>
          <div className="file-detail-row">
            <div className="file-detail-label">
              <span className="label">File Year & Month</span>
            </div>
            {canEdit ? (
              <div className="file-year-month-calendar">
                {dateSaving && (
                  <i className="pi pi-spin pi-spinner calendar-loader"></i>
                )}
                <Calendar
                  value={fileMonthYear}
                  disabled={dateSaving}
                  onChange={(e: any) => {
                    const date = (e?.value as Date | null) ?? null;
                    handleMonthYearChange(date);
                  }}
                  view="month"
                  dateFormat="M yy"
                  className="w-full "
                  placeholder="Select date"
                />
              </div>
            ) : (
              <div className="file-detail-value">
                {fileMonth && fileYear
                  ? `${months[Number(fileMonth) - 1]} ${fileYear}`
                  : "-"}
              </div>
            )}
          </div>
          <NonEditableField
            label="Uploaded By"
            value={
              data?.createdByData
                ? `${data.createdByData.first_name} ${data.createdByData.last_name} (${data.createdByData.email})`
                : null
            }
          />
          <NonEditableField
            label="Upload Date"
            value={
              data?.createdAt ? String(formatDate(data.createdAt) ?? "") : null
            }
          />
          <NonEditableField
            label="Last Modified"
            value={lastModified ? String(formatDate(lastModified) ?? "") : null}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {!hideTrigger && (
        <i
          className="pi pi-info-circle cursor-pointer action-icon"
          onClick={() =>
            isControlled ? onVisibleChange?.(true) : setOpen(true)
          }
        />
      )}
      <Sidebar
        position="right"
        className="description__sidebar"
        header={customHeader}
        visible={sidebarVisible}
        onHide={() => setSidebarVisible(false)}
      >
        <div className="description__body">
          <div className="description__actions">
            <CustomeButton
              label="View in Browser"
              variant="container"
              icon="pi pi-external-link"
              iconPos="right"
              onClick={handleViewInBrowser}
            />
            <CustomeButton
              label="Download File"
              variant="container"
              icon="pi pi-download"
              iconPos="right"
              onClick={handleDownloadFile}
            />
          </div>

          {isGxmAdmin ? (
            <TabView
              activeIndex={activeTab}
              onTabChange={(e) => setActiveTab(e.index)}
              className="description__tabs"
            >
              <TabPanel header="Details">{detailsContent}</TabPanel>
              <TabPanel header="History">
                <DocumentHistory
                  attachmentId={id}
                  blobFilter={data?.blobName ?? data?.name}
                  objectType={type}
                  fileName={safeDecode(
                    data?.displayName ||
                      data?.metadata?.display_name ||
                      data?.original_file_name ||
                      data?.name ||
                      fileName ||
                      "",
                  ).replace(/\(\(([^)]+)\)\)/g, "($1)")}
                  enabled={isGxmAdmin && activeTab === 1}
                />
              </TabPanel>
            </TabView>
          ) : (
            <div className="description__tabs">{detailsContent}</div>
          )}
        </div>
      </Sidebar>
    </>
  );
};

export default DocumentDescription;
